/**
 * @fileoverview Toast Notifications Store
 *
 * Manages application-wide toast notifications for user feedback.
 * Provides helper methods for success, error, warning, and info messages.
 */

import { defineStore } from 'pinia'
import { ref } from 'vue'

/**
 * @typedef {Object} Notification
 * @property {string} id - Unique notification ID
 * @property {'success'|'error'|'warning'|'info'} type - Notification type
 * @property {string} title - Notification title
 * @property {string} message - Notification message
 * @property {number} duration - Duration in milliseconds before auto-dismiss
 * @property {number} timestamp - Timestamp when notification was created
 */

/**
 * Generate unique notification ID
 * @returns {string} Unique ID
 */
function generateId() {
  return `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Pinia store for toast notifications
 *
 * @example
 * import { useNotificationsStore } from '@/stores/notifications'
 *
 * const notificationsStore = useNotificationsStore()
 * notificationsStore.success('Lab created successfully')
 * notificationsStore.error('Failed to delete lab', 'Error')
 */
export const useNotificationsStore = defineStore('notifications', () => {
  // State
  /** @type {import('vue').Ref<Notification[]>} */
  const notifications = ref([])

  // Actions

  /**
   * Add a notification to the queue
   *
   * @param {Object} options - Notification options
   * @param {'success'|'error'|'warning'|'info'} options.type - Notification type
   * @param {string} options.message - Notification message
   * @param {string} [options.title] - Notification title (optional)
   * @param {number} [options.duration=5000] - Duration in ms before auto-dismiss
   * @returns {string} Notification ID
   * @example
   * notificationsStore.addNotification({
   *   type: 'success',
   *   title: 'Success',
   *   message: 'Operation completed',
   *   duration: 3000
   * })
   */
  function addNotification({ type, title, message, duration = 5000 }) {
    const id = generateId()

    const notification = {
      id,
      type,
      title: title || '',
      message,
      duration,
      timestamp: Date.now()
    }

    notifications.value.push(notification)

    // Auto-remove after duration
    if (duration > 0) {
      setTimeout(() => {
        removeNotification(id)
      }, duration)
    }

    return id
  }

  /**
   * Remove a notification from the queue
   *
   * @param {string} id - Notification ID
   * @example
   * notificationsStore.removeNotification('notif-123')
   */
  function removeNotification(id) {
    const index = notifications.value.findIndex(n => n.id === id)
    if (index !== -1) {
      notifications.value.splice(index, 1)
    }
  }

  /**
   * Clear all notifications
   *
   * @example
   * notificationsStore.clearAll()
   */
  function clearAll() {
    notifications.value = []
  }

  /**
   * Show success notification
   *
   * Helper method for success messages with green styling.
   *
   * @param {string} message - Success message
   * @param {string} [title='Success'] - Title (optional)
   * @param {number} [duration=5000] - Duration in ms
   * @returns {string} Notification ID
   * @example
   * notificationsStore.success('Lab created successfully')
   * notificationsStore.success('Saved', 'Settings updated', 3000)
   */
  function success(message, title = 'Success', duration = 5000) {
    return addNotification({
      type: 'success',
      title,
      message,
      duration
    })
  }

  /**
   * Show error notification
   *
   * Helper method for error messages with red styling.
   *
   * @param {string} message - Error message
   * @param {string} [title='Error'] - Title (optional)
   * @param {number} [duration=7000] - Duration in ms (longer for errors)
   * @returns {string} Notification ID
   * @example
   * notificationsStore.error('Failed to delete lab')
   * notificationsStore.error('Invalid credentials', 'Authentication Failed')
   */
  function error(message, title = 'Error', duration = 7000) {
    return addNotification({
      type: 'error',
      title,
      message,
      duration
    })
  }

  /**
   * Show warning notification
   *
   * Helper method for warning messages with yellow/orange styling.
   *
   * @param {string} message - Warning message
   * @param {string} [title='Warning'] - Title (optional)
   * @param {number} [duration=6000] - Duration in ms
   * @returns {string} Notification ID
   * @example
   * notificationsStore.warning('Lab will expire in 1 hour')
   * notificationsStore.warning('Session expiring soon', 'Warning', 10000)
   */
  function warning(message, title = 'Warning', duration = 6000) {
    return addNotification({
      type: 'warning',
      title,
      message,
      duration
    })
  }

  /**
   * Show info notification
   *
   * Helper method for informational messages with blue styling.
   *
   * @param {string} message - Info message
   * @param {string} [title='Info'] - Title (optional)
   * @param {number} [duration=5000] - Duration in ms
   * @returns {string} Notification ID
   * @example
   * notificationsStore.info('Lab provisioning started')
   * notificationsStore.info('Check your email', 'Verification Sent')
   */
  function info(message, title = 'Info', duration = 5000) {
    return addNotification({
      type: 'info',
      title,
      message,
      duration
    })
  }

  return {
    // State
    notifications,

    // Actions
    addNotification,
    removeNotification,
    clearAll,

    // Helper methods
    success,
    error,
    warning,
    info
  }
})
